// -----------------------------------------------------------------------------
// ui/dom.ts — minimal DOM construction helpers.
//
// This is the closest thing we have to a "view framework", and it is deliberately
// tiny (CLAUDE.md §2/§4: no runtime UI framework). `el()` builds an element with
// attributes/events/children in one call; `clear()` empties a node.
//
// SECURITY: text is always set via textContent / text nodes — never innerHTML —
// so anything derived from user input (filenames, overlay text) cannot inject
// markup (CLAUDE.md §9).
// -----------------------------------------------------------------------------

export type Attrs = Record<string, string | number | boolean | EventListener | undefined | null>;
export type Child = Node | string | number | null | undefined | false;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs?: Attrs,
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (attrs) {
    for (const [key, value] of Object.entries(attrs)) {
      if (value == null || value === false) continue;
      if (key === 'class') node.className = String(value);
      else if (key === 'text') node.textContent = String(value);
      else if (key === 'html') throw new Error('el(): raw html is forbidden; use text.');
      else if (key.startsWith('on') && typeof value === 'function') {
        node.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
      } else if (typeof value === 'boolean') {
        if (value) node.setAttribute(key, '');
      } else {
        node.setAttribute(key, String(value));
      }
    }
  }
  appendChildren(node, children);
  return node;
}

export function appendChildren(node: Element, children: Child[]): void {
  for (const child of children) {
    if (child == null || child === false) continue;
    node.append(child instanceof Node ? child : document.createTextNode(String(child)));
  }
}

// Remove every child of a node (used when re-rendering a panel from state).
export function clear(node: Node): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}
